import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect, useCallback } from "react";
import { Toast } from "primereact/toast";
import useOrder from "hooks/useOrder";
import useCity from "hooks/useCity";
import { setPage } from "store/orderSlice";
import MonthFilter from "components/Ui/MonthFilter/MonthFilter";
import DateTimeFilter from "components/Ui/DateTimeFilter/DateTimeFilter";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan, faMotorcycle, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { Form } from "react-bootstrap";
import Order from "./Order/Order";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import ListLoadError from "components/Ui/ListLoadError/ListLoadError";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import { ORDER_COLUMNS } from "./Order/orderColumns";
import PercentageSummaryCard from "components/Ui/PercentageSummaryCard/PercentageSummaryCard";
import useCommissionDefaults from "hooks/useCommissionDefaults";
import { useGetVendorsDropdown, vendorDropdownKeys } from "hooks/useVendors";
import { useGetUsersDropdown } from "hooks/useUser";
import Select from "components/Ui/SelectField/SelectField";
import AddVendorQuickOrder from "./AddVendorQuickOrder/AddVendorQuickOrder";
import AddServiceVendorQuickOrder from "./AddServiceVendorQuickOrder/AddServiceVendorQuickOrder";
import AddAdminQuickOrder from "./AddAdminQuickOrder/AddAdminQuickOrder";
import EditOrder from "./EditOrder/EditOrder";
import SendVendorStatementModal from "./SendVendorStatementModal/SendVendorStatementModal";
import RunningOrdersToggle, {
    RUNNING_ORDERS_PAGE_SIZE,
    RunningOrdersNotice,
    isRunningOnly,
    storeRunningChoice,
} from "./RunningOrdersToggle/RunningOrdersToggle";
import AutoAdvanceToggle from "./AutoAdvanceToggle/AutoAdvanceToggle";
import { updateVendorOrderPercentage } from "services/vendors";
import { ORDER_STATUS_CHOICES as status } from "constants/orderStatus";
import {
    formatStatNumber,
    getBaselinePercentage,
    parsePercentageInput,
    toPercentageInputValue,
} from "utils/percentage";
import { formatMoney } from "utils/money";
import { readApiError } from "utils/apiError";

/**
 * /orders — the vendor-audience order screen, and the operational centre of the
 * dashboard. Route `orders/*`, gated on `super_admin || roles.manage_orders`
 * (App.js).
 *
 * DUAL AUDIENCE: this one component serves two completely different users.
 * Platform staff run the whole city from here; a vendor-role user
 * (`adminInfo.role === "vendor"` — a restaurant owner logged into the same
 * dashboard) sees a stripped-down version of the same screen showing only their
 * own orders. There is no separate vendor screen, which is why almost every
 * control below is wrapped in a role check.
 *
 * ROLE FORKS (all keyed on `adminInfo.role !== "vendor"`), i.e. WHAT VENDORS
 * CANNOT SEE OR DO:
 * - the المطاعم and السائق filters — a vendor cannot pivot to another vendor,
 *   and cannot filter by driver at all;
 * - the الطلبات المحذوفة filter — a vendor cannot reveal orders that were
 *   hidden from them at settlement, so their view of a settled period is
 *   permanently shorter than the operator's;
 * - the نسبة المطعم card — vendors never see the commission percentage being
 *   applied to them;
 * - the إرسال كشف حساب statement sender and the AutoAdvanceToggle;
 * - per-row edit, hard delete and "حذف للمطعم فقط" (see Order/Order.js);
 * - the driver select and customer notes on each row.
 * The city filter is a separate fork: only `super_admin` may choose a city; any
 * other admin is pinned to `adminInfo.cityId`.
 *
 * THE VENDOR SWEEP IS NARROWER THAN THE LIST, and not by anything sent from
 * here. "حذف طلبات المطعم" forwards the filter bar, and the API then restricts a
 * vendor sweep to orders the vendor was actually billed for — marking orders paid
 * cannot include ones that never reached a statement. So it may touch fewer
 * orders than the screen shows. There is nothing to configure client-side, by
 * design: a rule protecting a vendor's money should not be a parameter this app
 * could omit.
 *
 * The role fork is presentational only — it hides controls, it does not scope
 * data. Row scoping is the API's job, driven by the caller's token. Note that a
 * vendor-role user can still put `?vendor=<id>` in the URL and this component
 * will forward it, because `buildOrderParams` reads the URL unconditionally.
 *
 * ORDER-ENTRY FORK: one green button, always labelled "طلب سريع", opening one of
 * three modals. Staff get AddAdminQuickOrder (transcribed sale, type="whatsapp",
 * counted in vendor statements); a vendor flagged `isServiceVendor` gets
 * AddServiceVendorQuickOrder (one field, "go fetch this"); any other vendor gets
 * AddVendorQuickOrder (driver request). The audiences never overlap, so the
 * shared label is unambiguous — a user only ever sees one of the three.
 *
 * All three POST to the same api/order/dispatch with NO discriminator; the API
 * classifies the row from the caller. The fork here decides which FIELDS to
 * collect, never what kind of order results — that is the server's call, and
 * keeping it there is why a client cannot mis-file an order by sending the wrong
 * field. See each modal for the field-level detail.
 *
 * MIXED TYPES: no `type` filter is ever sent, so customer-app orders, vendor
 * driver requests and transcribed WhatsApp orders are interleaved in one table.
 * The only visual distinction is the per-type icon in the id cell. /orders and
 * /deliveryOrders show the SAME rows; they differ in default soft-delete
 * filter, columns and available actions.
 *
 * THE STATS ROW SHOWS TWO POPULATIONS, side by side and labelled as such:
 * "عدد الطلبات" / "إجمالي الشحن" cover every order matching the filters, while
 * "الطلبات المحتسبة" / "الإجمالي المحتسب" cover only the rows a vendor statement
 * would charge for (`counts_in_vendor_statement`, minus cancelled/deleted). The
 * API returns both sets — see getAllOrders' RESPONSE note. They are not a
 * statement preview: the statement also skips already-billed orders, which this
 * page expresses as the "الطلبات المحذوفة" filter, so the two agree on the
 * default view and deliberately diverge once that filter is widened.
 *
 * SETTLEMENT TOOLING: the نسبة المطعم card multiplies the API's `billedTotal`
 * aggregate (all filtered BILLABLE orders, not just the page) by the percentage. That
 * product is displayed and then discarded — only the percentage itself can be
 * persisted, via "حفظ للمطعم", and it merely becomes the default that seeds the
 * statement modal. The actual billing action is "حذف طلبات المطعم", which
 * bulk-soft-deletes everything matching the CURRENT filters; that is why the
 * button only appears once a vendor filter is active.
 *
 * KNOWN CONSTRAINTS (documented, not fixed):
 * - This page keeps separate foreground (`fetchOrders`) and silent
 *   (`refetchOrders`) paths so socket-driven refreshes (`props.newOrder`) can
 *   update without showing the first-load spinner. The branching stays local
 *   here, so the hook still does not know why a given refetch happened.
 * - `useOrder` independently derives the same params from the URL; the hook's
 *   copy is what post-mutation refetches use, so it can lag one render behind
 *   the params this component fetched with.
 * - The status list comes from constants/orderStatus.js so every order surface
 *   receives the same API value and Arabic label.
 * - Page size is hardcoded to 12 here and again inside `useOrder`.
 */
const Orders = (props) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCity = searchParams.get("city");
    const resolvedCityId = adminInfo?.super_admin ? selectedCity || undefined : adminInfo?.cityId;
    // The rate that applies before anything is picked. The dashboard holds no
    // default of its own — see services/commissionDefaults.js.
    const { data: commissionDefaults } = useCommissionDefaults(resolvedCityId);

    const { vendors } = useGetVendorsDropdown({
        cityId: resolvedCityId,
    });
    const { users } = useGetUsersDropdown({
        role: "delivery",
        cityId: resolvedCityId,
    });
    const queryClient = useQueryClient();
    // Empty until a restaurant is chosen: the rate now comes from the API,
    // not from a constant this app keeps.
    const [percentage, setPercentage] = useState("");
    const [persistedPercentage, setPersistedPercentage] = useState(null);
    const [savingPercentage, setSavingPercentage] = useState(false);
    const [deleteAllOrders, setDeleteAllOrders] = useState(false);
    const [deleteForType, setDeleteForType] = useState(null);
    const [singleSoftDeleteFor, setSingleSoftDeleteFor] = useState(null);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showVendorQuickOrder, setShowVendorQuickOrder] = useState(false);
    const [showAddServiceVendorOrder, setShowAddServiceVendorOrder] = useState(false);
    const [showAdminQuickOrder, setShowAdminQuickOrder] = useState(false);
    const [showSendStatement, setShowSendStatement] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("orders", ORDER_COLUMNS);
    const {
        orders,
        loading,
        listError,
        fetchOrders,
        fetchOrderStats,
        deleteOrder,
        deleteOrders,
        softDeleteOrder,
        editOrder,
        refetchOrders,
    } = useOrder();
    // Show the full-page spinner only on the first load. On later refetches
    // (search/filter/pagination) keep the list — and the search box — mounted
    // so typing doesn't unmount and refocus the input.
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!loading) setHasLoaded(true);
    }, [loading]);
    const page = useSelector((state) => state.order.page);
    const pages = useSelector((state) => state.order.pages);
    const count = useSelector((state) => state.order.count);
    const total = useSelector((state) => state.order.total);
    const shipping = useSelector((state) => state.order.shipping);
    // The billable slice of the same filters (counts_in_vendor_statement, minus
    // cancelled/deleted statuses) — what a statement over this view would charge
    // for. Shown next to the unfiltered count so the two populations are visibly
    // different numbers rather than one figure meaning whichever the reader assumed.
    const billedCount = useSelector((state) => state.order.billedCount);
    const billedTotal = useSelector((state) => state.order.billedTotal);
    // Fetch cities into Redux so the city filter is populated even on a direct
    // load of /orders (nothing else on this page fetches them).
    const { cities } = useCity();
    const dispatch = useDispatch();
    const toast = useRef(null);
    const [params, setParams] = useState(null);
    const previousParamsKey = useRef(null);
    const previousStatsParamsKey = useRef(null);
    const previousNewOrder = useRef(props.newOrder);

    // Derives the request params from the URL (the source of truth for filters,
    // so a filtered view can be bookmarked or shared) plus the Redux page.
    // Deliberately duplicated inside useOrder — see that hook's JSDoc for which
    // copy wins where.
    const buildOrderParams = useCallback(() => {
        // «عرض الطلبات الجارية فقط» shows every running order on one page.
        const nextParams = isRunningOnly(searchParams)
            ? { page: 1, size: RUNNING_ORDERS_PAGE_SIZE }
            : { page, size: 12 };

        if (searchParams.get("vendor")) {
            nextParams.vendorId = searchParams.get("vendor");
        }
        if (searchParams.get("delivery")) {
            nextParams.deliveryId = searchParams.get("delivery");
        }
        if (searchParams.get("status")) {
            nextParams.status = searchParams.get("status");
        }
        if (searchParams.get("startDate")) {
            nextParams.startDate = searchParams.get("startDate");
        }
        if (searchParams.get("endDate")) {
            nextParams.endDate = searchParams.get("endDate");
        }
        if (searchParams.get("search")) {
            nextParams.search = searchParams.get("search");
        }
        // «عرض الطلبات الجارية فقط». The API owns the list of statuses.
        if (isRunningOnly(searchParams)) {
            nextParams.inProgress = "true";
        }
        if (resolvedCityId) {
            nextParams.cityId = resolvedCityId;
        }

        // "الطلبات المحذوفة" filter — sourced from the `deleted_for_vendor`
        // boolean. Default: hide deleted. "all" → show both. "true" → only
        // deleted.
        const deletedFilter = searchParams.get("deletedForVendor");
        if (deletedFilter === "true") {
            nextParams.deletedForVendor = "true";
        } else if (deletedFilter !== "all") {
            nextParams.deletedForVendor = "false";
        }

        return nextParams;
    }, [page, resolvedCityId, searchParams]);

    // A vendor-role user IS the selected vendor — there is no picker for them,
    // so the percentage context is synthesised from their own profile. For
    // staff the selection comes from the URL, which means the percentage card
    // and the statement button stay inert until a vendor filter is chosen.
    const selectedVendor =
        adminInfo.role === "vendor"
            ? {
                  id: adminInfo.id,
                  name: adminInfo.name,
                  order_percentage: adminInfo.order_percentage ?? null,
              }
            : vendors?.find((vendor) => String(vendor.id) === String(searchParams.get("vendor")));

    const selectedVendorPersistedPercentage = parsePercentageInput(
        selectedVendor?.order_percentage,
    );
    // What the server resolved — the restaurant's own rate, or its city's
    // default. See api util/commissionRates.js.
    const selectedVendorEffectivePercentage = selectedVendor?.effective_order_percentage;
    const currentPercentage = parsePercentageInput(percentage);
    // The commission basis is the BILLABLE subtotal, not every order on screen.
    // Multiplying the percentage by `total` quoted a figure the statement would
    // never charge, since orders flagged out of statements — and cancelled ones —
    // were still in that sum.
    const percentageBase = Number(billedTotal || 0);
    const percentageAmount = (percentageBase * (currentPercentage ?? 0)) / 100;
    const percentageBaseline = getBaselinePercentage(
        persistedPercentage,
        selectedVendorEffectivePercentage,
    );
    const isPercentageDirty =
        currentPercentage !== null && currentPercentage !== percentageBaseline;
    const canPersistPercentage =
        adminInfo.role !== "vendor" && Boolean(selectedVendor?.id) && currentPercentage !== null;
    const percentageHint = !selectedVendor?.id
        ? "يمكنك تعديل القيمة مؤقتاً الآن. اختر مطعماً أولاً إذا أردت حفظها كقيمة افتراضية."
        : currentPercentage === null
          ? "أدخل نسبة صالحة بين 0 و100 للحفظ."
          : persistedPercentage !== null && !isPercentageDirty
            ? "هذه القيمة محفوظة بالفعل. يمكنك الضغط للحفظ مرة أخرى."
            : "التعديل يعمل فوراً في هذه الشاشة فقط، ولن يُحفظ إلا بعد الضغط على زر الحفظ.";

    useEffect(() => {
        setPersistedPercentage(selectedVendorPersistedPercentage);
        setPercentage(
            toPercentageInputValue(
                getBaselinePercentage(
                    selectedVendorPersistedPercentage,
                    selectedVendorEffectivePercentage,
                ),
            ),
        );
    }, [selectedVendor?.id, selectedVendorPersistedPercentage]);

    useEffect(() => {
        const newParams = buildOrderParams();
        const nextParamsKey = JSON.stringify(newParams);
        const statsParams = { ...newParams };
        delete statsParams.page;
        delete statsParams.size;
        const nextStatsParamsKey = JSON.stringify(statsParams);
        const paramsChanged = previousParamsKey.current !== nextParamsKey;
        const statsParamsChanged = previousStatsParamsKey.current !== nextStatsParamsKey;
        const newOrderChanged = previousNewOrder.current !== props.newOrder;

        previousParamsKey.current = nextParamsKey;
        previousStatsParamsKey.current = nextStatsParamsKey;
        previousNewOrder.current = props.newOrder;
        setParams(newParams);

        if (statsParamsChanged) {
            fetchOrderStats(statsParams);
        }

        if (paramsChanged) {
            fetchOrders(newParams);
            return;
        }

        if (newOrderChanged) {
            refetchOrders(newParams);
        }
    }, [buildOrderParams, fetchOrderStats, fetchOrders, props.newOrder, refetchOrders]);

    const handleChangePage = (event, newPage) => {
        dispatch(setPage(newPage));
    };

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    const handleSearch = (value) => {
        if (value) {
            searchParams.set("search", value);
        } else {
            searchParams.delete("search");
        }
        setSearchParams(searchParams);
        dispatch(setPage(1));
    };

    const setSelectedOrderValue = (vendor) => {
        setSelectedOrder(vendor);
        setDeleteAllOrders(false);
        setDeleteForType(null);
        setSingleSoftDeleteFor(null);
    };

    const toggleDelete = () => {
        setShowDelete((pre) => (pre = !pre));
    };

    const toggleEdit = () => {
        setShowEdit((pre) => (pre = !pre));
    };

    const confirmDelete = async () => {
        // deleteOrder returns `true`, or the axios error. The error object is
        // truthy, so `if (response)` read every failure as a success — a
        // refused delete said «تم حذف الطلب بنجاح» (admin#70). The API refuses a
        // running order with 409 and says why; the dialog stays open.
        const response = await deleteOrder(selectedOrder.id);
        const failure = readApiError(response, "تعذّر حذف الطلب");
        if (failure) {
            showMessage("error", "هناك خطأ", failure);
            return;
        }
        showMessage("warn", "تم الحذف", "تم حذف الطلب بنجاح");
        closeDeleteDialog();
    };

    const handlePercentageChange = (event) => {
        const nextValue = event.target.value;

        if (nextValue === "") {
            setPercentage("");
            return;
        }

        const normalizedValue = parsePercentageInput(nextValue);

        if (normalizedValue === null) {
            return;
        }

        setPercentage(toPercentageInputValue(normalizedValue));
    };

    const resetPercentage = () => {
        setPercentage(
            toPercentageInputValue(
                getBaselinePercentage(persistedPercentage, selectedVendorEffectivePercentage),
            ),
        );
    };

    /**
     * Saves the typed percentage as the vendor's stored default
     * (PATCH vendor/:userId/order-percentage).
     *
     * Only the PERCENTAGE is persisted — never the computed amount shown on the
     * card, and nothing links it to the orders currently filtered. Its only
     * downstream effect is seeding the statement modal's percentage box, which
     * is why the card's hint text stresses that editing is screen-local until
     * this button is pressed. Vendor-role users can never reach it
     * (`canPersistPercentage` requires a non-vendor role).
     */
    const persistPercentage = async () => {
        if (!selectedVendor?.id || currentPercentage === null) {
            return;
        }

        setSavingPercentage(true);

        try {
            const response = await updateVendorOrderPercentage(
                selectedVendor.id,
                currentPercentage,
            );
            const nextPersistedPercentage = parsePercentageInput(
                response.order_percentage ?? currentPercentage,
            );

            setPersistedPercentage(nextPersistedPercentage);
            setPercentage(
                toPercentageInputValue(
                    getBaselinePercentage(
                        nextPersistedPercentage,
                        selectedVendorEffectivePercentage,
                    ),
                ),
            );
            await queryClient.invalidateQueries({ queryKey: vendorDropdownKeys.all });
            showMessage("success", "تم الحفظ", "تم حفظ نسبة المطعم بنجاح");
        } catch (err) {
            showMessage(
                "error",
                "تعذر الحفظ",
                err.response?.data?.message || "حدث خطأ أثناء حفظ نسبة المطعم",
            );
        } finally {
            setSavingPercentage(false);
        }
    };

    /**
     * "حذف طلبات المطعم" — the billing cycle's closing move: hide every order
     * matching the CURRENT filters from the vendor's view, marking that period
     * as settled. The soft-delete flag is the only record that settlement
     * happened; no statement row is required.
     *
     * CONSTRAINT: the scope is `params`, i.e. the whole filtered set across all
     * pages — not the twelve rows on screen. An operator who forgets a date
     * range hides the vendor's entire history in one click. The button is only
     * rendered while a vendor filter is active, which is the sole guard against
     * an unbounded delete.
     */
    const deleteAll = async () => {
        try {
            // `params` carries the current filter bar, so the sweep starts from
            // the set on screen — then narrows further, see below.
            const response = await deleteOrders({ ...params, deleteFor: deleteForType });

            if (response?.ok) {
                // api#167: running orders are never swept. Say how many stayed.
                const kept = response.skippedRunning
                    ? ` — لم تُحذف ${response.skippedRunning} من الطلبات الجارية، ألغِها أولًا`
                    : "";
                showMessage("warn", "تم الحذف", `تم حذف الطلبات بنجاح${kept}`);
                closeDeleteDialog();
            } else {
                showMessage("error", "هناك خطأ", response?.message || "حدث خطأ غير متوقع");
            }
        } catch (err) {
            console.error("Error deleting order:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    /**
     * Soft-deletes a single order from vendor view only, while keeping the order for other audiences.
     *
     * @param {object} order - Order row payload.
     */
    const deleteSingleForVendor = async (order) => {
        if (!order) {
            showMessage("error", "هناك خطأ", "لا يوجد طلب محدد");
            return;
        }

        try {
            const response = await softDeleteOrder(order.id, "vendor");

            if (response?.ok) {
                showMessage("warn", "تم الحذف", "تم حذف الطلب للمطعم");
                closeDeleteDialog();
                return;
            }

            // api#167: a running order is refused with its reason.
            showMessage("error", "لم يُحذف الطلب", response?.message || "حدث خطأ غير متوقع");
        } catch {
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    const requestSingleSoftDeleteForVendor = (order) => {
        setSelectedOrder(order);
        setDeleteAllOrders(false);
        setDeleteForType(null);
        setSingleSoftDeleteFor("vendor");
        setShowDelete(true);
    };

    const confirmSingleSoftDelete = async () => {
        await deleteSingleForVendor(selectedOrder);
    };

    const closeDeleteDialog = () => {
        setShowDelete(false);
        setDeleteAllOrders(false);
        setDeleteForType(null);
        setSingleSoftDeleteFor(null);
    };

    const vendorOptions = vendors?.map((v) => ({
        value: v.id,
        label: v.name,
    }));

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {loading && !hasLoaded ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3 filters orders-filters">
                        {adminInfo.role !== "vendor" ? (
                            <>
                                <div className="col">
                                    <h6>المطاعم</h6>
                                    <Select
                                        options={[{ value: "", label: "الكل" }, ...vendorOptions]}
                                        placeholder="اختر المطعم"
                                        value={
                                            vendorOptions.find(
                                                (v) => v.value === +searchParams.get("vendor"),
                                            ) || { value: "", label: "الكل" }
                                        }
                                        isSearchable
                                        onChange={(selected) => {
                                            // The billing view: let the running
                                            // default decide (see isRunningOnly).
                                            searchParams.delete("running");
                                            if (!selected || selected.value === "") {
                                                searchParams.delete("vendor");
                                            } else {
                                                searchParams.set("vendor", selected.value);
                                            }
                                            setSearchParams(searchParams);
                                        }}
                                    />
                                </div>
                                <div className="col">
                                    <h6>السائق</h6>
                                    <Form.Select
                                        value={searchParams.get("delivery") || ""}
                                        onChange={(e) => {
                                            // The billing view: let the running
                                            // default decide (see isRunningOnly).
                                            searchParams.delete("running");
                                            if (e.target.value === "") {
                                                searchParams.delete("delivery");
                                                setSearchParams(searchParams);
                                            } else {
                                                searchParams.set("delivery", e.target.value);
                                                setSearchParams(searchParams);
                                            }
                                        }}
                                    >
                                        <option value={""}>الكل</option>
                                        {users?.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </>
                        ) : null}

                        {adminInfo?.super_admin ? (
                            <div className="col">
                                <h6>المدينه</h6>
                                <Form.Select
                                    value={searchParams.get("city") || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "") {
                                            searchParams.delete("city");
                                            setSearchParams(searchParams);
                                        } else {
                                            searchParams.set("city", e.target.value);
                                            setSearchParams(searchParams);
                                        }
                                    }}
                                >
                                    <option value={""}>الكل</option>
                                    {cities?.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        ) : null}
                        <div className="col">
                            <h6>الحاله</h6>
                            <Form.Select
                                value={searchParams.get("status") || ""}
                                onChange={(e) => {
                                    // A status filter decides the rows itself, so
                                    // the running default steps aside for it.
                                    searchParams.delete("running");
                                    if (e.target.value === "") {
                                        searchParams.delete("status");
                                        setSearchParams(searchParams);
                                    } else {
                                        searchParams.set("status", e.target.value);
                                        setSearchParams(searchParams);
                                    }
                                }}
                            >
                                <option value={""}>الكل</option>
                                {status?.map((s) => (
                                    <option key={s.en} value={s.en}>
                                        {s.ar}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        {adminInfo.role !== "vendor" ? (
                            <div className="col">
                                <h6>الطلبات المحذوفة</h6>
                                <Form.Select
                                    defaultValue={searchParams.get("deletedForVendor") || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "") {
                                            searchParams.delete("deletedForVendor");
                                        } else {
                                            searchParams.set("deletedForVendor", e.target.value);
                                        }
                                        setSearchParams(searchParams);
                                    }}
                                >
                                    <option value={""}>إخفاء المحذوفة</option>
                                    <option value="all">عرض الطلبات المحذوفة ايضا</option>
                                    <option value="true">عرض الطلبات المحذوفة فقط</option>
                                </Form.Select>
                            </div>
                        ) : null}
                        <MonthFilter
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => dispatch(setPage(1))}
                        />
                        <DateTimeFilter
                            paramKey="startDate"
                            label="من"
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => dispatch(setPage(1))}
                        />
                        <DateTimeFilter
                            paramKey="endDate"
                            label="الى"
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => dispatch(setPage(1))}
                        />
                    </div>
                    {/* With the switch on, the cards count only running orders.
                        Say so, and offer the way back to all of them. */}
                    {isRunningOnly(searchParams) ? (
                        <RunningOrdersNotice
                            onShowAll={() => {
                                storeRunningChoice(false);
                                searchParams.set("running", "0");
                                setSearchParams(searchParams);
                            }}
                        />
                    ) : null}
                    <div className="stats-container2 stats-container2--four">
                        <div className="stat-box">
                            <h4>عدد الطلبات</h4>
                            <p>{formatStatNumber(count)}</p>
                            <span className="stat-box__hint">
                                {isRunningOnly(searchParams)
                                    ? "من الطلبات الجارية فقط"
                                    : "كل الطلبات المطابقة للفلاتر"}
                            </span>
                        </div>

                        <div className="stat-box">
                            <h4>الطلبات المحتسبة</h4>
                            <p>{formatStatNumber(billedCount)}</p>
                            <span className="stat-box__hint">
                                {isRunningOnly(searchParams)
                                    ? "من الجارية فقط، التي تدخل في كشف حساب المطعم"
                                    : "التي تدخل في كشف حساب المطعم"}
                            </span>
                        </div>

                        <div className="stat-box">
                            <h4>الإجمالي المحتسب (₪)</h4>
                            <p>{formatMoney(billedTotal)}</p>
                            <span className="stat-box__hint">
                                {isRunningOnly(searchParams)
                                    ? "إجمالي الطلبات الجارية"
                                    : "إجمالي كل الطلبات"}
                                : {formatMoney(total)}
                            </span>
                        </div>

                        <div className="stat-box">
                            <h4>إجمالي الشحن (₪)</h4>
                            <p>{formatMoney(shipping)}</p>
                            <span className="stat-box__hint">
                                {isRunningOnly(searchParams)
                                    ? "للطلبات الجارية فقط"
                                    : "لكل الطلبات"}
                            </span>
                        </div>
                    </div>
                    {adminInfo.role !== "vendor" ? (
                        <div className="percentage-panel-row">
                            <PercentageSummaryCard
                                title="نسبة المطعم"
                                contextName={selectedVendor?.name}
                                savedPercentage={persistedPercentage}
                                baselineValue={percentageBaseline}
                                fallbackValue={commissionDefaults?.vendor ?? null}
                                currentValue={percentage}
                                onChange={handlePercentageChange}
                                onReset={resetPercentage}
                                onPersist={persistPercentage}
                                isSaving={savingPercentage}
                                canPersist={canPersistPercentage}
                                saveLabel="حفظ للمطعم"
                                emptySelectionHint="اختر مطعماً لحفظ النسبة"
                                basisLabel="إجمالي الطلبات المحتسبة"
                                basisValue={percentageBase}
                                resultValue={percentageAmount}
                                isDirty={isPercentageDirty}
                                inputId="vendor-order-percentage"
                                hintText={percentageHint}
                            />
                        </div>
                    ) : null}
                    <div className="controls">
                        <SearchInput
                            initialValue={searchParams.get("search") || ""}
                            onSearch={handleSearch}
                            placeholder="بحث بالاسم، رقم الطلب أو الهاتف"
                            minWidth={330}
                        />
                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                marginRight: "auto",
                                alignItems: "center",
                            }}
                        >
                            <RunningOrdersToggle
                                checked={isRunningOnly(searchParams)}
                                onChange={(next) => {
                                    // Explicit both ways, and kept for the
                                    // next visit to the page.
                                    storeRunningChoice(next);
                                    searchParams.set("running", next ? "1" : "0");
                                    setSearchParams(searchParams);
                                }}
                            />
                            {adminInfo.role !== "vendor" ? (
                                <AutoAdvanceToggle
                                    cityId={resolvedCityId}
                                    onMessage={showMessage}
                                />
                            ) : null}
                            {adminInfo.role !== "vendor" && params?.vendorId && selectedVendor ? (
                                <button
                                    onClick={() => setShowSendStatement(true)}
                                    style={{
                                        backgroundColor: BRAND,
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                    إرسال كشف حساب
                                </button>
                            ) : null}
                            {params?.vendorId ? (
                                <button
                                    onClick={() => {
                                        setDeleteAllOrders(true);
                                        setDeleteForType("vendor");
                                        toggleDelete();
                                    }}
                                    style={{
                                        backgroundColor: "red",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrashCan} />
                                    حذف طلبات المطعم
                                </button>
                            ) : null}
                            {/* ONE BUTTON, THREE MODALS. Every manual order-entry
                                surface reads "طلب سريع" — unambiguous because
                                these audiences never overlap, so a given user
                                only ever sees one of them.

                                Which modal opens is decided the same way the API
                                decides the order's policy: staff transcribe a
                                sale (type="whatsapp", counted in statements);
                                a service vendor logs "go fetch this"; any other
                                vendor requests a driver. All three post to the
                                same api/order/dispatch with no discriminator —
                                the server classifies from the caller.

                                isServiceVendor arrives on the auth payload. A
                                MISSING flag must fall back to the full delivery
                                form: that is what every vendor has today, and an
                                older API build simply omits the key. */}
                            {adminInfo.role === "vendor" ? (
                                <button
                                    onClick={() => {
                                        if (adminInfo.isServiceVendor === true) {
                                            setShowAddServiceVendorOrder(true);
                                        } else {
                                            setShowVendorQuickOrder(true);
                                        }
                                    }}
                                    style={{
                                        backgroundColor: "green",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faMotorcycle} />
                                    طلب سريع
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        setShowAdminQuickOrder(true);
                                    }}
                                    style={{
                                        backgroundColor: "green",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faWhatsapp} />
                                    طلب سريع
                                </button>
                            )}
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>
                    <div className="table-container">
                        <table>
                            <TableHead visibleColumns={visibleColumns} />
                            {/* After a failed load, the rows in the store are
                                the rows of other filters. Do not show them. */}
                            {orders.length && !listError ? (
                                <tbody>
                                    {orders.map((order) => (
                                        <Order
                                            key={order.id}
                                            order={order}
                                            visibleColumns={visibleColumns}
                                            setSelectedOrder={setSelectedOrder}
                                            setSelectedOrderValue={setSelectedOrderValue}
                                            toggleDelete={toggleDelete}
                                            editOrder={editOrder}
                                            users={users}
                                            toggleEdit={toggleEdit}
                                            showMessage={showMessage}
                                            onRequestDeleteForVendor={
                                                requestSingleSoftDeleteForVendor
                                            }
                                        />
                                    ))}
                                </tbody>
                            ) : listError ? null : (
                                <h4>لا يوجد طلبات!</h4>
                            )}
                        </table>
                        {/* A failed load is not an empty list. */}
                        {listError ? (
                            <ListLoadError
                                message={listError}
                                onRetry={() => fetchOrders(params)}
                                isRetrying={loading}
                            />
                        ) : null}
                    </div>
                    {/* No page buttons while every running order is on one page. */}
                    {isRunningOnly(searchParams) ? null : (
                        <Stack spacing={1}>
                            <Pagination
                                count={pages}
                                page={page}
                                shape="rounded"
                                color={BRAND}
                                style={{ margin: "20px auto 0" }}
                                onChange={handleChangePage}
                            />
                        </Stack>
                    )}
                    <DeleteItem
                        show={showDelete}
                        close={closeDeleteDialog}
                        loading={loading}
                        confirm={
                            deleteAllOrders
                                ? deleteAll
                                : singleSoftDeleteFor === "vendor"
                                  ? confirmSingleSoftDelete
                                  : confirmDelete
                        }
                    />
                    <AddVendorQuickOrder
                        show={showVendorQuickOrder}
                        close={() => {
                            setShowVendorQuickOrder(false);
                        }}
                        showMessage={showMessage}
                    />
                    <AddServiceVendorQuickOrder
                        show={showAddServiceVendorOrder}
                        close={() => {
                            setShowAddServiceVendorOrder(false);
                        }}
                        showMessage={showMessage}
                    />
                    <EditOrder
                        show={showEdit}
                        close={() => {
                            setShowEdit(false);
                        }}
                        showMessage={showMessage}
                        order={selectedOrder}
                    />
                    <AddAdminQuickOrder
                        show={showAdminQuickOrder}
                        close={() => {
                            setShowAdminQuickOrder(false);
                        }}
                        showMessage={showMessage}
                        vendors={vendors}
                        vendorOptions={vendorOptions}
                        drivers={users}
                        cities={cities}
                    />
                    <SendVendorStatementModal
                        show={showSendStatement}
                        close={() => setShowSendStatement(false)}
                        vendor={selectedVendor}
                        showMessage={showMessage}
                        initialStartDate={searchParams.get("startDate") || undefined}
                        initialEndDate={searchParams.get("endDate") || undefined}
                    />
                </>
            )}
        </div>
    );
};
export default Orders;
